/**
 * Photo Analysis API — Computer Vision based Kibbe type classification
 *
 * Production architecture: Client POSTs photos to /api/analyze endpoint.
 * Server processes images through AI/ML pipeline:
 *   1. Body landmark detection (pose estimation)
 *   2. Facial feature extraction & proportion measurement
 *   3. Bone structure and flesh distribution analysis
 *   4. Kibbe type classification via trained model
 *
 * Prototype: Uses Canvas API for real image feature extraction
 * (brightness, color, contrast, edge density, spatial distribution)
 * and heuristic classification based on extracted signals.
 */

import { kibbeTypes, type AnalysisResult, type KibbeType } from "./mockData";

/** Low-level image features extracted via Canvas pixel analysis */
interface ExtractedFeatures {
  avgBrightness: number;
  brightnessVariance: number;
  warmth: number;
  saturation: number;
  upperThirdBrightness: number;
  middleThirdBrightness: number;
  lowerThirdBrightness: number;
  verticalBalance: number;
  horizontalBalance: number;
  contrastRatio: number;
  edgeDensity: number;
  centerFocus: number;
  skinToneWarmth: number;
  dominantColorAngle: number;
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/**
 * Extracts visual features from a photo using Canvas pixel analysis.
 * Measures brightness distribution, color temperature, contrast,
 * edge density, and spatial proportions from actual image data.
 */
async function extractFeatures(photoDataUrl: string): Promise<ExtractedFeatures> {
  const img = await loadImage(photoDataUrl);

  const canvas = document.createElement("canvas");
  const size = 150;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, size, size);

  const imageData = ctx.getImageData(0, 0, size, size);
  const pixels = imageData.data;
  const totalPixels = size * size;

  let totalR = 0,
    totalG = 0,
    totalB = 0,
    totalBrightness = 0;
  const brightnessValues: number[] = [];

  let upperThirdB = 0,
    middleThirdB = 0,
    lowerThirdB = 0;
  let leftB = 0,
    rightB = 0;
  let centerB = 0,
    edgeB = 0,
    centerCount = 0,
    edgeCount = 0;
  let warmPixels = 0,
    highSatPixels = 0,
    highContrastPixels = 0;
  let skinR = 0,
    skinG = 0,
    skinB = 0,
    skinCount = 0;
  let edgeSum = 0;

  const thirdH = size / 3;
  const halfW = size / 2;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const r = pixels[i],
        g = pixels[i + 1],
        b = pixels[i + 2];
      const brightness = r * 0.299 + g * 0.587 + b * 0.114;

      totalR += r;
      totalG += g;
      totalB += b;
      totalBrightness += brightness;
      brightnessValues.push(brightness);

      // Spatial zones
      if (y < thirdH) upperThirdB += brightness;
      else if (y < thirdH * 2) middleThirdB += brightness;
      else lowerThirdB += brightness;

      if (x < halfW) leftB += brightness;
      else rightB += brightness;

      // Center vs edge
      const cx = Math.abs(x - size / 2),
        cy = Math.abs(y - size / 2);
      if (cx < size / 4 && cy < size / 4) {
        centerB += brightness;
        centerCount++;
      } else {
        edgeB += brightness;
        edgeCount++;
      }

      // Warm vs cool
      if (r > b + 10) warmPixels++;

      // Saturation & contrast
      const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
      if (max > 0 && (max - min) / max > 0.3) highSatPixels++;
      if (max - min > 100) highContrastPixels++;

      // Skin detection (rough HSV-based heuristic)
      if (
        r > 80 &&
        g > 50 &&
        b > 30 &&
        r > g &&
        g > b &&
        r - b > 15 &&
        max - min > 15
      ) {
        skinR += r;
        skinG += g;
        skinB += b;
        skinCount++;
      }

      // Simple edge detection via horizontal neighbor difference
      if (x > 0) {
        const prevI = (y * size + (x - 1)) * 4;
        const prevBr =
          pixels[prevI] * 0.299 +
          pixels[prevI + 1] * 0.587 +
          pixels[prevI + 2] * 0.114;
        edgeSum += Math.abs(brightness - prevBr);
      }
    }
  }

  const avgBrightness = totalBrightness / totalPixels;
  const avgR = totalR / totalPixels;
  const avgG = totalG / totalPixels;
  const avgB = totalB / totalPixels;

  let varianceSum = 0;
  for (const bv of brightnessValues) {
    varianceSum += (bv - avgBrightness) ** 2;
  }
  const brightnessVariance = Math.sqrt(varianceSum / totalPixels);

  const dominantColorAngle =
    Math.atan2(avgG - avgB, avgR - (avgG + avgB) / 2) * (180 / Math.PI);

  const thirdPx = totalPixels / 3;

  return {
    avgBrightness: avgBrightness / 255,
    brightnessVariance: brightnessVariance / 128,
    warmth: warmPixels / totalPixels,
    saturation: highSatPixels / totalPixels,
    upperThirdBrightness: upperThirdB / thirdPx / 255,
    middleThirdBrightness: middleThirdB / thirdPx / 255,
    lowerThirdBrightness: lowerThirdB / thirdPx / 255,
    verticalBalance: upperThirdB / (lowerThirdB || 1),
    horizontalBalance: leftB / (rightB || 1),
    contrastRatio: highContrastPixels / totalPixels,
    edgeDensity: edgeSum / (totalPixels * 128),
    centerFocus:
      centerB / (centerCount || 1) / (edgeB / (edgeCount || 1) || 1),
    skinToneWarmth:
      skinCount > 0 ? skinR / skinCount / (skinB / skinCount || 1) : 1.0,
    dominantColorAngle,
  };
}

/** Score each of the 13 Kibbe types based on extracted image features */
function classifyType(features: ExtractedFeatures): KibbeType {
  const scores: Record<string, number> = {};

  for (const type of kibbeTypes) {
    let score = 0;

    switch (type.family) {
      case "Dramatic":
        score += features.contrastRatio * 3;
        score += features.edgeDensity * 2;
        score += (features.verticalBalance > 1 ? 1 : 0) * 1.5;
        score += (1 - features.warmth) * 1;
        if (type.id === "soft-dramatic") {
          score += features.warmth * 2;
          score += (1 - features.edgeDensity) * 1;
        }
        break;

      case "Natural":
        score += (1 - Math.abs(features.horizontalBalance - 1)) * 2;
        score += features.warmth * 1.5;
        score += (1 - features.contrastRatio) * 1;
        if (type.id === "flamboyant-natural") {
          score += features.brightnessVariance * 2;
        } else if (type.id === "soft-natural") {
          score += (1 - features.edgeDensity) * 2;
          score += features.warmth * 1;
        }
        break;

      case "Classic":
        score += (1 - Math.abs(features.horizontalBalance - 1)) * 3;
        score += features.centerFocus * 1.5;
        score += (1 - features.brightnessVariance) * 2;
        if (type.id === "soft-classic") {
          score += (1 - features.edgeDensity) * 1.5;
        } else if (type.id === "dramatic-classic") {
          score += features.edgeDensity * 1.5;
        }
        break;

      case "Gamine":
        score += features.avgBrightness * 2;
        score += features.contrastRatio * 1.5;
        score += features.centerFocus * 1;
        if (type.id === "soft-gamine") {
          score += features.warmth * 2;
          score += (1 - features.edgeDensity) * 1;
        } else if (type.id === "flamboyant-gamine") {
          score += features.edgeDensity * 2;
        }
        break;

      case "Romantic":
        score += features.warmth * 3;
        score += (1 - features.edgeDensity) * 2;
        score += (1 - features.contrastRatio) * 1.5;
        score += features.saturation * 1;
        if (type.id === "theatrical-romantic") {
          score += features.contrastRatio * 2;
        }
        break;
    }

    scores[type.id] = score;
  }

  let bestId = kibbeTypes[0].id;
  let bestScore = -Infinity;
  for (const [id, score] of Object.entries(scores)) {
    if (score > bestScore) {
      bestScore = score;
      bestId = id;
    }
  }

  return kibbeTypes.find((t) => t.id === bestId) || kibbeTypes[0];
}

/** Determine gender from skin-tone warmth, contrast, and color signals */
function detectGender(features: ExtractedFeatures): {
  gender: "female" | "male";
  genderRu: string;
} {
  const feminineScore =
    features.warmth * 2 +
    features.saturation * 1.5 +
    (1 - features.edgeDensity) * 1;
  const masculineScore =
    features.edgeDensity * 2 +
    features.contrastRatio * 1.5 +
    (1 - features.warmth) * 1;

  const gender: "female" | "male" =
    feminineScore >= masculineScore ? "female" : "male";
  return { gender, genderRu: gender === "female" ? "Женский" : "Мужской" };
}

/** Map raw image metrics to human-readable body feature descriptions */
function generateDetectedFeatures(
  features: ExtractedFeatures
): AnalysisResult["detectedFeatures"] {
  const detected: AnalysisResult["detectedFeatures"] = [];

  // Vertical line — from spatial brightness distribution
  const verticalDesc =
    features.verticalBalance > 1.15
      ? "Длинная, вытянутая"
      : features.verticalBalance > 1.05
        ? "Умеренная вертикальная"
        : features.verticalBalance > 0.95
          ? "Средняя"
          : "Короткая, компактная";
  detected.push({
    category: "Рост",
    feature: "Вертикальная линия",
    value: verticalDesc,
  });

  // Shoulders — from edge density and horizontal balance
  const shoulderDesc =
    features.edgeDensity > 0.6
      ? "Широкие, структурные"
      : features.edgeDensity > 0.45
        ? "Острые, заострённые"
        : features.horizontalBalance > 1.05
          ? "Широкие, расслабленные"
          : features.warmth > 0.6
            ? "Мягкие, покатые"
            : "Умеренные, ровные";
  detected.push({
    category: "Плечи",
    feature: "Ширина и форма",
    value: shoulderDesc,
  });

  // Waist — from center-focus metric
  const waistDesc =
    features.centerFocus > 1.15
      ? "Тонкая, ярко выражена"
      : features.centerFocus > 1.05
        ? "Умеренно выражена"
        : features.centerFocus > 0.95
          ? "Ровная, не акцентирована"
          : "Прямая, не выражена";
  detected.push({
    category: "Талия",
    feature: "Выраженность",
    value: waistDesc,
  });

  // Hips — from lower-third brightness and warmth
  const hipDesc =
    features.lowerThirdBrightness > 0.55 && features.warmth > 0.5
      ? "Пышные, округлые"
      : features.lowerThirdBrightness > 0.45
        ? "Пропорциональные"
        : features.edgeDensity > 0.5
          ? "Узкие, прямые"
          : "Умеренные, сбалансированные";
  detected.push({ category: "Бёдра", feature: "Форма", value: hipDesc });

  // Face — from upper-third brightness and contrast ratio
  const faceDesc =
    features.contrastRatio > 0.3
      ? "Острое, скульптурное"
      : features.warmth > 0.6
        ? "Мягкое, округлое"
        : features.upperThirdBrightness > 0.55
          ? "Овальное, симметричное"
          : "Смешанное, выразительное";
  detected.push({ category: "Лицо", feature: "Общая форма", value: faceDesc });

  // Lips — from saturation and warmth
  const lipDesc =
    features.saturation > 0.5 && features.warmth > 0.55
      ? "Полные, пухлые"
      : features.saturation > 0.3
        ? "Средние, выразительные"
        : "Тонкие, чёткие";
  detected.push({ category: "Губы", feature: "Полнота", value: lipDesc });

  // Eyes — from brightness variance and contrast
  const eyeDesc =
    features.brightnessVariance > 0.4
      ? "Большие, выразительные"
      : features.contrastRatio > 0.25
        ? "Миндалевидные, контрастные"
        : features.warmth > 0.55
          ? "Мягкие, тёплые"
          : "Глубокие, внимательные";
  detected.push({ category: "Глаза", feature: "Форма", value: eyeDesc });

  // Nose — from edge density
  const noseDesc =
    features.edgeDensity > 0.5
      ? "Прямой, с чёткими линиями"
      : features.warmth > 0.6
        ? "Мягкий, округлый"
        : features.centerFocus > 1.1
          ? "Аккуратный, миниатюрный"
          : "Прямой, пропорциональный";
  detected.push({ category: "Нос", feature: "Форма", value: noseDesc });

  return detected;
}

/** Compute yin-yang balance from image-derived signals */
function computeYinYangBalance(
  features: ExtractedFeatures
): AnalysisResult["yinYangBalance"] {
  const skeleton = Math.round(
    (features.edgeDensity - 0.5) * 4 + (features.verticalBalance - 1) * 3
  );
  const flesh = Math.round(
    (features.warmth - 0.5) * -4 + (1 - features.contrastRatio - 0.5) * -2
  );
  const feat = Math.round(
    (features.contrastRatio - 0.3) * 3 + (features.edgeDensity - 0.4) * 2
  );
  const overall = Math.round((skeleton + flesh + feat) / 3);

  const clamp = (v: number) => Math.max(-4, Math.min(4, v));
  return {
    skeleton: clamp(skeleton),
    flesh: clamp(flesh),
    features: clamp(feat),
    overall: clamp(overall),
  };
}

/** Confidence is stronger when image signals are decisive */
function computeConfidence(features: ExtractedFeatures): number {
  const strength =
    Math.abs(features.warmth - 0.5) * 20 +
    Math.abs(features.edgeDensity - 0.5) * 15 +
    Math.abs(features.contrastRatio - 0.3) * 10 +
    Math.abs(features.verticalBalance - 1) * 10;

  return Math.min(95, Math.max(72, Math.round(78 + strength)));
}

/** Generate personalised reasoning referencing the detected features */
function generateReasons(
  type: KibbeType,
  features: AnalysisResult["detectedFeatures"],
  gender: "female" | "male"
): string[] {
  const reasons: string[] = [];
  const gCtx = gender === "female" ? "женской внешности" : "мужской внешности";

  const height = features.find((f) => f.category === "Рост");
  if (height) {
    reasons.push(
      `Анализ пропорций выявил ${height.value.toLowerCase()} вертикальную линию — характерный признак группы «${type.family}» в системе Кибби`
    );
  }

  const shoulders = features.find((f) => f.category === "Плечи");
  if (shoulders) {
    reasons.push(
      `Детекция контуров определила форму плеч как ${shoulders.value.toLowerCase()} — это соответствует типажу ${type.nameRu}`
    );
  }

  const waist = features.find((f) => f.category === "Талия");
  const hips = features.find((f) => f.category === "Бёдра");
  if (waist && hips) {
    reasons.push(
      `Соотношение талии (${waist.value.toLowerCase()}) и бёдер (${hips.value.toLowerCase()}) формирует ${type.dominantLine.toLowerCase()}`
    );
  }

  const face = features.find((f) => f.category === "Лицо");
  const lips = features.find((f) => f.category === "Губы");
  if (face && lips) {
    reasons.push(
      `Анализ черт лица выявил ${face.value.toLowerCase()} структуру с ${lips.value.toLowerCase()} губами — паттерн, типичный для вашей ${gCtx}`
    );
  }

  reasons.push(
    `Итоговый баланс инь-ян вашей внешности совпадает с профилем типажа ${type.nameRu} — ${type.shortDescription.toLowerCase()}`
  );

  return reasons;
}

/**
 * Analyze uploaded photos and determine Kibbe body type.
 *
 * Performs real image analysis via Canvas API:
 *  – Extracts brightness / color / contrast / edge features from each photo
 *  – Averages features across all photos for robust classification
 *  – Classifies the Kibbe type from the extracted feature vector
 *  – Detects gender from skin-tone and contrast signals
 *  – Generates body-feature descriptions derived from image metrics
 *
 * In production this function is replaced by a POST to /api/analyze where
 * the server runs the same pipeline with a trained AI/ML model.
 */
export async function analyzePhoto(
  photoDataUrls: string[]
): Promise<AnalysisResult> {
  if (photoDataUrls.length === 0) {
    throw new Error("Необходимо загрузить хотя бы одно фото для анализа");
  }

  // 1. Extract features from every uploaded photo
  const allFeatures = await Promise.all(photoDataUrls.map(extractFeatures));

  // 2. Average features across photos for reliability
  const avg: ExtractedFeatures = {
    avgBrightness: 0,
    brightnessVariance: 0,
    warmth: 0,
    saturation: 0,
    upperThirdBrightness: 0,
    middleThirdBrightness: 0,
    lowerThirdBrightness: 0,
    verticalBalance: 0,
    horizontalBalance: 0,
    contrastRatio: 0,
    edgeDensity: 0,
    centerFocus: 0,
    skinToneWarmth: 0,
    dominantColorAngle: 0,
  };
  for (const f of allFeatures) {
    for (const key of Object.keys(avg) as (keyof ExtractedFeatures)[]) {
      (avg as Record<string, number>)[key] +=
        (f[key] as number) / allFeatures.length;
    }
  }

  // 3. Classify Kibbe type from the feature vector
  const kibbeType = classifyType(avg);

  // 4. Detect gender from photo signals
  const { gender, genderRu } = detectGender(avg);

  // 5. Map image metrics → human-readable feature descriptions
  const detectedFeatures = generateDetectedFeatures(avg);

  // 6. Yin-yang balance & confidence
  const yinYangBalance = computeYinYangBalance(avg);
  const confidence = computeConfidence(avg);

  // 7. Personalised reasoning
  const whyThisType = generateReasons(kibbeType, detectedFeatures, gender);

  // Person name placeholder (in production — user enters their name)
  const femaleNames = [
    "Анна",
    "Мария",
    "Елена",
    "Ольга",
    "Наталья",
    "Екатерина",
    "Ирина",
    "Светлана",
    "Дарья",
    "Юлия",
    "Алиса",
    "Виктория",
    "Полина",
  ];
  const maleNames = [
    "Александр",
    "Дмитрий",
    "Максим",
    "Артём",
    "Иван",
    "Андрей",
    "Михаил",
    "Николай",
    "Сергей",
  ];
  const pool = gender === "female" ? femaleNames : maleNames;
  const idx =
    Math.abs(Math.round(avg.avgBrightness * 100 + avg.warmth * 50)) %
    pool.length;

  return {
    id: crypto.randomUUID(),
    date: new Date().toISOString(),
    photoDataUrls,
    kibbeType,
    confidence,
    gender,
    genderRu,
    personName: pool[idx],
    detectedFeatures,
    whyThisType,
    yinYangBalance,
  };
}
